import { EnrollmentStatus, ProgramType } from './interfaces/registration.interface';
import { AppTheme } from './services/theme';

export const DEFAULT_BASE_THEME: AppTheme = 'light' as const;
export const PAYMENT_REMINDER_TEMPLATE_NAME = 'paymentReminderEmail';
export const YOUTUBE_THUMBNAIL_PLACEHOLDER = 'https://placehold.co/600x400?text=Course+Introduction+Video';
export const DEFAULT_PROFILE_AVATAR = 'assets/images/default-avatar.jpg';
export const DEFAULT_COURSE_THUMBNAIL = 'assets/images/course-default-thumbnail.png';
export const FACULTY_PORTAL_URL = 'https://dev.eo.coditas.org';
export const PARTICIPANTS_PORTAL_URL = 'https://student-portal.eo.coditas.org';

export enum LIST_TYPE {
    COURSE = 'course',
    PROGRAM = 'program',
}

export enum UserRole {
    FACULTY = 'FACULTY',
    ADMIN = 'ADMIN',
    HOD = 'HOD',
    DEAN_EO = 'DEAN_EO',
    DEAN_FA = 'DEAN_FACULTY_AFFAIRS',
    DEAN = 'DEAN_EO,DEAN_FA',
    ASSISTANT = 'ASSISTANT',
    SUPER_ADMIN = 'SUPER_ADMIN',
}

export enum FEE_TYPE {
    OPEN = 'OPEN',
    IN_HOUSE = 'IN_HOUSE',
    FREE = 'FREE',
    FLAT = 'FLAT',
}

export const USER_ROLE_LABELS: Record<UserRole, string> = {
    [UserRole.FACULTY]: 'Faculty',
    [UserRole.ADMIN]: 'Admin',
    [UserRole.HOD]: 'HoD',
    [UserRole.DEAN_EO]: 'Dean',
    [UserRole.DEAN_FA]: 'Dean Faculty Affairs',
    [UserRole.DEAN]: 'Dean',
    [UserRole.ASSISTANT]: 'Assistant',
    [UserRole.SUPER_ADMIN]: 'Super Admin',
};

export const FILE_SYSTEM_CONFIG = {
    PROGRAM: {
        KEY: 'creditsfile',
        BASE_FOLDER_NAME: 'COURSE',
    },
    PROFILE: {
        KEY: 'profilePicture',
        BASE_FOLDER_NAME: 'USER',
    },
};

export enum BASE_TABS {
    PENDING = 'pending',
    APPROVED = 'approved',
    DRAFTS = 'drafts',
    PUBLISHED = 'published',
    COMPLETED = 'completed',
    REJECTED = 'rejected',
    CANCELLED = 'cancelled',
}

export enum PARENT_TABS {
    MY_COURSES = 'my-courses',
    PROGRAMS = 'programs',
    APPROVAL_COURSES = 'approval-courses',
    APPROVAL_PROGRAMS = 'approval-programs',
}

export enum REGISTRATION_REQUEST_TABS {
    MY_COURSE_REQUESTS = 'my-course-reqeusts',
    FACULTY_COURSE_REQUESTS = 'faculty-course-requests',
    MY_PROGRAM_REQUESTS = 'my-program-requests',
    FACULTY_PROGRAM_REQUESTS = 'faculty-program-requests',
}

export enum APPROVAL_TABS {
    FOR_REVIEW = 'for-review',
    APPROVED = 'approved',
    LIVE = 'live',
    COMPLETED = 'completed',
}

export const PROGRAM_TYPE_LABELS: Record<ProgramType, string> = {
    [ProgramType.OPEN]: 'Open',
    [ProgramType.IN_HOUSE]: 'In House',
    [ProgramType.CLOSED]: 'Closed',
    [ProgramType.NPTL]: 'NPTL',
};

export enum COURSE_STATUS {
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    PUBLISHED = 'PUBLISHED',
    COMPLETED = 'COMPLETED',
    DRAFT = 'DRAFT',
    CHANGES_REQUESTED = 'CHANGES_REQUESTED',
    IN_REVIEW = 'IN_REVIEW',
    REJECTED = 'REJECTED',
    REFUND = 'REFUND',
    REGISTERED = 'REGISTERED',
    REFUNDED = 'REFUNDED',
    REVIEW = 'REVIEW',
    CANCELLED = 'CANCELLED',
}

export enum FEEDBACK_TABS {
    LIVE = 'live',
    SCHEDULED = 'scheduled',
}

export enum FEEDBACK_PARENT_TABS {
    MY_COURSE_FEEDBACK = 'my-course-feedback',
    FACULTY_COURSE_FEEDBACK = 'faculty-course-feedback',
    MY_PROGRAM_FEEDBACK = 'my-program-feedback',
    FACULTY_PROGRAM_FEEDBACK = 'faculty-program-feedback',
}

export const COURSE_STATUS_LABELS: Record<COURSE_STATUS | APPROVAL_STATUS, string> = {
    [COURSE_STATUS.PENDING]: 'In Review',
    [COURSE_STATUS.APPROVED]: 'Approved',
    [COURSE_STATUS.PUBLISHED]: 'Live',
    [COURSE_STATUS.COMPLETED]: 'Completed',
    [COURSE_STATUS.DRAFT]: 'Draft',
    [COURSE_STATUS.CHANGES_REQUESTED]: 'Changes Requested',
    [COURSE_STATUS.IN_REVIEW]: 'In Review',
    [COURSE_STATUS.REJECTED]: 'Rejected',
    [COURSE_STATUS.REFUND]: 'Refund',
    [COURSE_STATUS.REGISTERED]: 'Registered',
    [COURSE_STATUS.REFUNDED]: 'Refunded',
    [COURSE_STATUS.REVIEW]: 'Review',
    [COURSE_STATUS.CANCELLED]: 'Cancelled',
};

export enum APPROVAL_STATUS {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    PENDING = 'PENDING',
    REFUND = 'REFUND',
    REGISTERED = 'REGISTERED',
    REFUNDED = 'REFUNDED',
    REVIEW = 'REVIEW',
}

export enum COURSE_APPROVAL_STATUS {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    PENDING = 'PENDING',
}

export enum APPROVER_LEVEL {
    HOD = 'HOD',
    DEAN_FA = 'DEAN_FA',
    DEAN_EO = 'DEAN_EO',
}

export const APPROVER_LEVEL_LABELS: Record<APPROVER_LEVEL, string> = {
    [APPROVER_LEVEL.HOD]: 'HOD',
    [APPROVER_LEVEL.DEAN_FA]: 'Dean FA',
    [APPROVER_LEVEL.DEAN_EO]: 'Dean EO',
};

export const ENROLLMENT_STATUS_MAP: Record<EnrollmentStatus, string> = {
    [EnrollmentStatus.APPROVED]: 'Approved',
    [EnrollmentStatus.REJECTED]: 'Rejected',
    [EnrollmentStatus.REFUND]: 'Refunded',
    [EnrollmentStatus.REGISTERED]: 'Registered',
    [EnrollmentStatus.ONGOING]: 'Registered',
    [EnrollmentStatus.PAYMENT_PROCESSING]: 'Approved',
    [EnrollmentStatus.COMPLETED]: 'Registered',
    [EnrollmentStatus.PAYMENT_FAILED]: 'Approved',
    [EnrollmentStatus.IN_REVIEW]: 'In Review',
    [EnrollmentStatus.REFUND_ISSUED]: 'Refunded',
};

export const getStatusClass = (status: EnrollmentStatus): string => {
    switch (ENROLLMENT_STATUS_MAP[status]) {
        case ENROLLMENT_STATUS_MAP[EnrollmentStatus.REGISTERED] || // NOSONAR
            ENROLLMENT_STATUS_MAP[EnrollmentStatus.COMPLETED] ||
            ENROLLMENT_STATUS_MAP[EnrollmentStatus.ONGOING]:
            return 'status-registered';
        case ENROLLMENT_STATUS_MAP[EnrollmentStatus.IN_REVIEW]:
            return 'status-in-review';
        case ENROLLMENT_STATUS_MAP[EnrollmentStatus.APPROVED] || // NOSONAR
            ENROLLMENT_STATUS_MAP[EnrollmentStatus.PAYMENT_FAILED] ||
            ENROLLMENT_STATUS_MAP[EnrollmentStatus.PAYMENT_PROCESSING]:
            return 'status-approved';
        case ENROLLMENT_STATUS_MAP[EnrollmentStatus.ONGOING]:
            return 'status-ongoing';
        case ENROLLMENT_STATUS_MAP[EnrollmentStatus.REFUND] || ENROLLMENT_STATUS_MAP[EnrollmentStatus.REFUND_ISSUED]: // NOSONAR
            return 'status-refund-initiated';
        case ENROLLMENT_STATUS_MAP[EnrollmentStatus.REJECTED]:
            return 'status-rejected';
        case ENROLLMENT_STATUS_MAP[EnrollmentStatus.COMPLETED]:
            return 'status-completed';
        default:
            return 'status-in-review';
    }
};

export const mycourseApprovalFilterConfig = [
    {
        category: 'Published',
        fields: [{ type: 'multiselect', label: 'Published', options: ['Yes', 'No'], selected: [] }],
    },
    {
        category: 'Duration',
        fields: [
            { type: 'date', label: 'Select Start Date', key: 'startDate', value: '' },
            { type: 'date', label: 'Select End Date', key: 'endDate', value: '' },
        ],
    },
    {
        category: 'Feedback Form',
        fields: [{ type: 'multiselect', label: 'Feedback Form (Published)', options: ['Yes', 'No'], selected: [] }],
    },
];

export const mycourseFilterConfig = [
    {
        category: 'Type',
        fields: [{ type: 'multiselect', label: 'Type', options: [], selected: [] }],
    },
    {
        category: 'Published',
        fields: [{ type: 'multiselect', label: 'Published', options: ['Yes', 'No'], selected: [] }],
    },
    {
        category: 'Duration',
        fields: [
            { type: 'date', label: 'Select Start Date', key: 'startDate', value: '' },
            { type: 'date', label: 'Select End Date', key: 'endDate', value: '' },
        ],
    },
    {
        category: 'Feedback Form',
        fields: [{ type: 'multiselect', label: 'Feedback Form (Published)', options: ['Yes', 'No'], selected: [] }],
    },
];

export const programFilterConfig = [
    {
        category: 'Published',
        fields: [{ type: 'multiselect', label: 'Published', options: ['Yes', 'No'], selected: [] }],
    },
];

export const registrationFilterConfig = [
    {
        category: 'Duration',
        fields: [
            { type: 'date', label: 'Start Date', key: 'startDate', value: '' },
            { type: 'date', label: 'End Date', key: 'endDate', value: '' },
        ],
    },
    {
        category: 'Registered Count',
        fields: [
            {
                type: 'range',
                label: 'Filter Registered Count',
                key: 'registeredCount',
                min: 0,
                max: 200,
                step: 1,
                value: { min: 0, max: 200 },
            },
        ],
    },
    {
        category: 'Co-faculty',
        fields: [
            { type: 'multiselect', label: 'Co-faculty', key: 'facultyIds', options: [], selected: [], paginated: true },
        ],
    },
];

export const registrationDetailFilterConfig = [
    {
        category: 'Fee Category',
        fields: [
            {
                type: 'multiselect',
                label: 'Fee Category',
                key: 'feeCategory',
                options: ['All', 'Government Organisation', 'Industry', 'Overseas/ Foreign National', 'Student'],
                selected: [],
            },
        ],
    },
    {
        category: 'Highest Educational Experience',
        fields: [
            {
                type: 'multiselect',
                label: 'Highest Educational Experience',
                key: 'qualification',
                options: ['B.Tech ', 'M.Tech', 'MBA', 'M.Com', 'M.Sc', 'M.A', 'M.Phil', 'Ph.D'],
                selected: [],
            },
        ],
    },
    {
        category: 'Work Experience',
        fields: [
            {
                type: 'multiselect',
                label: 'Work Experience',
                key: 'experience',
                options: ['1-5 years', '5-10 years', '10-15 years', '15-20 years', '20+ years'],
                selected: [],
            },
        ],
    },
];

export const feedbackFilterConfig = [
    {
        category: 'Course Name',
        fields: [
            {
                type: 'multiselect',
                label: 'Course Name',
                key: 'courseName',
                options: [],
                selected: [],
                paginated: true,
            },
        ],
    },
    {
        category: 'Course Rating',
        fields: [
            {
                type: 'range',
                label: 'Course Rating',
                key: 'courseRating',
                min: 1,
                max: 5,
                step: 1,
                value: { min: 1, max: 5 },
            },
        ],
    },
    {
        category: 'No of Reviews',
        fields: [
            {
                type: 'range',
                label: 'No of Reviews',
                key: 'reviewCount',
                min: 1,
                max: 100,
                step: 1,
                value: { min: 1, max: 100 },
            },
        ],
    },
];

export const certificateFilterConfig = [
    {
        category: 'Certificate Type',
        fields: [
            {
                type: 'multiselect',
                label: 'Certificate Type',
                key: 'certificateType',
                options: ['Completion', 'Participation', 'No Participation'],
                selected: [],
            },
        ],
    },
    {
        category: 'Physical Certificate',
        fields: [
            {
                type: 'multiselect',
                label: 'Physical Certificate',
                key: 'isPhysical',
                options: ['Yes', 'No'],
                selected: [],
            },
        ],
    },
];

export const courseDeliveryOptions = [
    {
        label: 'Online',
        value: 'ONLINE',
    },
    {
        label: 'Offline',
        value: 'OFFLINE',
    },
];
